import styled from "@emotion/styled";
import { MDSFontWeights } from "@/mds/foundations";
import { MDSText, MDSTextVariant } from "@/mds/components/text";
import { MDSShortcutKeyComboSize, MDSShortcutKeyVariant } from "./shortcut-key.types";

export const SHORTCUT_KEY_FONT_WEIGHT = MDSFontWeights.medium;

const SHORTCUT_KEY_TEXT_FONT_SIZE_PX: Record<MDSShortcutKeyComboSize, number> = {
  Default: 11,
  Large: 14,
};

export const StyledShortcutKeyWrapper = styled.div<{
  $inline?: boolean;
  $isFixedWidth?: boolean;
  $size?: MDSShortcutKeyComboSize;
  $variant?: MDSShortcutKeyVariant;
}>(({ theme, $inline, $isFixedWidth, $size = "Default", $variant = "Default" }) => {
  if ($variant === "Tooltip") {
    return {
      display: $inline ? "inline-flex" : "flex",
      boxSizing: "border-box",
      justifyContent: "center",
      alignItems: "center",
      height: theme.lineHeights[MDSTextVariant.TextXs],
      minHeight: theme.lineHeights[MDSTextVariant.TextXs],
      maxHeight: theme.lineHeights[MDSTextVariant.TextXs],
      width: "auto",
      minWidth: "0px",
      maxWidth: undefined,
      flex: undefined,
      padding: theme.spacing["0"],
      borderRadius: theme.borderRadius.none,
      border: "none",
    };
  }

  const isLarge = $size === "Large";
  const baseSize = isLarge ? "20px" : "16px";
  const wrapperPadding = getShortcutKeyWrapperPadding({
    isFixedWidth: $isFixedWidth,
    isLarge,
  });

  return {
    display: $inline ? "inline-flex" : "flex",
    boxSizing: "border-box",
    justifyContent: "center",
    alignItems: "center",
    height: baseSize,
    minHeight: baseSize,
    maxHeight: baseSize,
    width: $isFixedWidth ? baseSize : "auto",
    minWidth: baseSize,
    maxWidth: $isFixedWidth ? baseSize : undefined,
    flex: $isFixedWidth ? `0 0 ${baseSize}` : undefined,
    padding: wrapperPadding,
    borderRadius: theme.borderRadius.small,
    border: `1px solid ${theme.colors.border.onAccent}`,
  };
});

function getShortcutKeyWrapperPadding({
  isFixedWidth,
  isLarge,
}: {
  isFixedWidth?: boolean;
  isLarge: boolean;
}) {
  if (isFixedWidth && isLarge) {
    return "2px 1px";
  }

  if (isFixedWidth) {
    return "0px";
  }

  if (isLarge) {
    return "2px 4px";
  }

  return "1px 3px";
}

export const StyledShortcutKeyText = styled(MDSText)<{
  $size?: MDSShortcutKeyComboSize;
  $textScale?: number;
  $textTranslateY?: string;
  $variant?: MDSShortcutKeyVariant;
}>(({ theme, $size = "Default", $textScale = 1, $textTranslateY, $variant = "Default" }) => {
  const isTooltip = $variant === "Tooltip";
  const baseFontSize = isTooltip ? 12 : SHORTCUT_KEY_TEXT_FONT_SIZE_PX[$size];
  const lineHeight = isTooltip
    ? theme.lineHeights[MDSTextVariant.TextXs]
    : theme.lineHeights[MDSTextVariant.TextS];
  const fontWeight = isTooltip
    ? theme.fontWeights.regular
    : $size === "Large"
      ? theme.fontWeights.regular
      : SHORTCUT_KEY_FONT_WEIGHT;

  return {
    display: "inline-block",
    fontSize: `${baseFontSize * $textScale}px`,
    lineHeight,
    transform: $textTranslateY ? `translateY(${$textTranslateY})` : undefined,
    fontWeight,
  };
});
